import React, { useContext } from "react";

import classnames from "classnames";

import { Trans } from "components/i18n/source";
import { PropertyListingContext } from "components/store/source";
import { Header, Link, Section, Text } from "components/ui/source";

import IconText from "../IconText";

import { policies } from "./policies";

import styles from "./BookingPolicies.module.scss";

interface BookingPoliciesProps {
  className?: string;
  id?: string;
}

export default function BookingPolicies({
  id,
  className,
}: BookingPoliciesProps) {
  const { listing } = useContext(PropertyListingContext);
  const policyData = {
    accessibilityInfo: listing?.accessibilityInfo,
    homeTruths: listing?.homeTruths,
    houseRules: listing?.houseRules,
  };
  const contactEmailStr = listing?.contactEmailStr;
  const contactEmailLtr = listing?.contactEmailLtr;

  // Show note for house rule
  const houseRuleNote = (
    <Text
      className={styles.note}
      types={["grey-1", "caption1"]}
      translateKey="listing:bookingPolicies.houseRules.note"
    />
  );

  const policyList = policies.map(policy => {
    if (policyData[policy.name]) {
      policy.description = policyData[policy.name];

      // Special case - parsing data into array - can we do this in the server?
      if (policy.name === "homeTruths") {
        policy.description = policyData[policy.name]
          .split(/\r?\n/)
          .filter(Boolean);
      }
    }

    return (
      <IconText key={policy.name} className={styles.policy} {...policy}>
        {policy.name === "houseRules" && houseRuleNote}
      </IconText>
    );
  });

  return (
    <Section id={id} className={classnames(styles.container, className)}>
      <Header
        className={styles.title_header}
        size="md"
        type="headline3"
        translateKey="listing:bookingPolicies.title"
      />
      {policyList}

      {contactEmailStr && contactEmailLtr && (
        <>
          <Text
            className={styles.contact_title}
            types={["body1", "medium"]}
            translateKey="listing:bookingPolicies.contact.title"
          />

          <Text
            types={["body1"]}
            className={styles.contact_less_than_90_days_description}
          >
            <Trans
              i18nKey="listing:bookingPolicies.contact.lessThanDaysDescription"
              components={{
                lessThanDaysDescriptionPhoneNumberLink: (
                  <Link
                    textElement="span"
                    href={"tel:1-617-300-0956"}
                    text={"1-617-300-0956"}
                    textTypes={["body1", "underline"]}
                  />
                ),
                lessThanDaysDescriptionEmailLink: (
                  <Link
                    textElement="span"
                    href={`mailto:${contactEmailStr}`}
                    text={contactEmailStr}
                    textTypes={["body1", "underline"]}
                  />
                ),
                lessThanDaysDescriptionContactInfoLink: (
                  <Link href={"/contact-info"} underline dark />
                ),
              }}
            />
          </Text>
          <Text types={["body1"]}>
            <Trans
              i18nKey="listing:bookingPolicies.contact.greaterThanDaysDescription"
              components={{
                greaterThanDaysDescriptionEmailLink: (
                  <Link
                    textElement="span"
                    href={`mailto:${contactEmailLtr}`}
                    text={contactEmailLtr}
                    textTypes={["body1", "underline"]}
                  />
                ),
              }}
            />
          </Text>
          <Link
            textElement="span"
            href={`mailto:${listing?.contactEmail}`}
            text={listing?.contactEmail}
            textTypes={["body1", "underline"]}
          />
        </>
      )}
    </Section>
  );
}
